import { useEffect, useSyncExternalStore } from 'react'
import { jwtDecode } from 'jwt-decode'

import { appConfig } from '../config'
import { getValidAccessToken } from '../features/auth'
import { useJWT } from './useJWT'

const listeners = new Set()

let permissionsState = {
	accessToken: null,
	permissions: [],
	loading: false,
	error: null,
	promise: null,
}

const emitChange = () => {
	listeners.forEach(listener => listener())
}

const subscribe = listener => {
	listeners.add(listener)
	return () => {
		listeners.delete(listener)
	}
}

const normalizePermissions = permissions => {
	if (!Array.isArray(permissions)) return []

	return permissions
		.filter(permission => typeof permission?.operation === 'string' && typeof permission?.target === 'string')
		.map(({ operation, target }) => ({ operation, target }))
}

const fetchPermissions = async () => {
	const accessToken = await getValidAccessToken()
	if (!accessToken) return []

	const { userID } = jwtDecode(accessToken)
	if (!userID) return []

	const response = await fetch(`${appConfig.identityService}/v2/users/${encodeURIComponent(userID)}/permissions`, {
		headers: {
			authorization: `Bearer ${accessToken}`,
		},
	})

	if (!response.ok) {
		throw new Error(`Permissions request failed with status ${response.status}`)
	}

	return normalizePermissions(await response.json())
}

const loadPermissions = accessToken => {
	if (!accessToken) {
		permissionsState = {
			accessToken: null,
			permissions: [],
			loading: false,
			error: null,
			promise: null,
		}
		emitChange()
		return
	}

	if (permissionsState.accessToken === accessToken) {
		return
	}

	const promise = fetchPermissions()

	permissionsState = {
		accessToken,
		permissions: [],
		loading: true,
		error: null,
		promise,
	}
	emitChange()

	promise
		.then(permissions => {
			if (permissionsState.promise !== promise) return
			permissionsState = {
				accessToken,
				permissions,
				loading: false,
				error: null,
				promise: null,
			}
			emitChange()
		})
		.catch(error => {
			if (permissionsState.promise !== promise) return
			permissionsState = {
				accessToken,
				permissions: [],
				loading: false,
				error,
				promise: null,
			}
			emitChange()
		})
}

const getSnapshot = () => permissionsState

export const useIdentityPermissions = () => {
	const { accessToken } = useJWT()
	const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

	useEffect(() => {
		loadPermissions(accessToken)
	}, [accessToken])

	if (accessToken && state.accessToken !== accessToken) {
		return {
			permissions: [],
			loading: true,
			error: null,
		}
	}

	return {
		permissions: state.permissions,
		loading: state.loading,
		error: state.error,
	}
}

export const resetIdentityPermissionsForTests = () => {
	permissionsState = {
		accessToken: null,
		permissions: [],
		loading: false,
		error: null,
		promise: null,
	}
	emitChange()
}
